import React from 'react';
import { Button } from '../ui/button';
import { useLanguage } from '../../context/LanguageContext';
import { translations, LINKS, getNimnyAppUrl } from '../../data/translations';

// Nimny hero illustration - new watermark image
const NIMNY_ILLUSTRATION = '/assets/migrated/95977040403b89b6.png';

const HeroSection = () => {
  const { language, t } = useLanguage();

  const trackClick = (buttonName) => {
    if (window.plausible) {
      window.plausible('Click', { props: { button: buttonName } });
    }
  };

  // Get the Create form URL based on language (absolute URL to nimny.app)
  const getCreateUrl = () => {
    if (language === 'ru') return 'https://nimny.app/ru/agent';
    if (language === 'es') return 'https://nimny.app/es/agent';
    return 'https://nimny.app/en/agent';
  };

  const buttonClass = "px-8 py-3 text-base font-sans rounded-xl transition-all duration-300 w-[180px]";

  return (
    <section className="relative min-h-[85vh] md:min-h-screen flex items-center justify-center overflow-hidden">
      {/* Transparent - stars show through from NightAmbience */}

      {/* Hero Content */}
      <div className="relative z-10 w-full max-w-3xl mx-auto px-4 md:px-6 pt-10 pb-0 md:py-16 text-center">
        
        {/* Nimny Illustration with warm glow */}
        <div className="relative mx-auto mb-4 md:mb-8 w-[200px] h-[200px] md:w-[320px] md:h-[320px]">
          {/* Soft warm glow behind the lantern */}
          <div 
            className="absolute inset-0"
            style={{
              background: 'radial-gradient(circle at 60% 70%, rgba(255,180,80,0.3) 0%, rgba(201,168,124,0.1) 40%, transparent 70%)',
              transform: 'scale(1.3)',
              filter: 'blur(25px)'
            }}
          />
          {/* Main illustration with floating animation */}
          <img 
            src={NIMNY_ILLUSTRATION}
            alt="Nimny"
            className="relative z-10 w-full h-full object-contain"
            loading="eager"
            fetchpriority="high"
            style={{ 
              animation: 'float 7s ease-in-out infinite'
            }}
          />
        </div>

        {/* Title - Patrick Hand, golden, reduced letter spacing */}
        <h1 
          className="text-2xl sm:text-3xl md:text-5xl text-[#c9a87c] mb-2 md:mb-4 leading-tight"
          style={{ 
            fontFamily: '"Patrick Hand", cursive',
            letterSpacing: '-0.02em'
          }}
        >
          {t(translations.hero.headline)}
        </h1>

        {/* Subtitle - warm beige reference style */}
        <p 
          className="font-serif text-base md:text-xl mb-6 md:mb-12 italic leading-relaxed text-[#F4E6C8]"
        >
          {t(translations.hero.subheadline)}
        </p>

        {/* CTA Buttons - Hierarchy: Primary (filled) > Accent (subtle fill) > Secondary (outline) */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4 mb-0 flex-wrap">
          {/* Read Online - PRIMARY: Filled gold */}
          <a 
            href={getNimnyAppUrl(language)}
            target="_blank" 
            rel="noopener noreferrer"
            onClick={() => trackClick('Read Online - Hero')}
          >
            <Button 
              className={`${buttonClass} bg-[#c9a87c] text-[#1a1a2e] font-medium hover:bg-[#d4b896] hover:shadow-[0_0_25px_rgba(201,168,124,0.5)]`}
              data-testid="hero-read-online"
            >
              {t(translations.nav.readOnline)}
            </Button>
          </a>

          {/* Create your story - ACCENT: Subtle gold fill with glow on hover */}
          <a 
            href={getCreateUrl()}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackClick('Create Story - Hero')}
          >
            <Button 
              className={`${buttonClass} bg-[#c9a87c]/15 border border-[#c9a87c]/50 text-[#c9a87c] font-medium hover:bg-[#c9a87c]/25 hover:border-[#c9a87c] hover:shadow-[0_0_20px_rgba(201,168,124,0.3)]`}
              data-testid="hero-create-story"
            >
              {t(translations.nav.createStory)}
            </Button>
          </a>

          {/* Read on Amazon - SECONDARY: Outline only (external link) */}
          <a 
            href={LINKS.amazon} 
            target="_blank" 
            rel="noopener noreferrer"
            onClick={() => trackClick('Read on Amazon - Hero')}
          >
            <Button 
              className={`${buttonClass} bg-transparent border border-[#c9a87c]/40 text-[#c9a87c]/80 hover:bg-[#c9a87c]/10 hover:border-[#c9a87c]/60 hover:text-[#c9a87c]`}
              data-testid="hero-read-amazon"
            >
              {t(translations.nav.readOnAmazon)}
            </Button>
          </a>
        </div>
      </div>

      {/* CSS for floating animation */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-8px) scale(1.02); }
        }
      `}</style>
    </section>
  );
};

export default HeroSection;
